// ══════════════════════════════════════════════════════════
//  #FLEET_MAPS_MINI_V1 (2026-10-09) — the two fleet maps, small, for dashboards that are not the Fleet dash.
//
//  Bill: "Is there a spot we could place this GPS Map on PM DASH with a KEY Moving etc? It could be small to open in
//  larger window but ability to close. Owners Dash... place both maps to the right row 1 & two."
//
//  One self-contained piece so a host screen only has to drop it in and hand it the storage reader:
//    <FleetMapsMini sg={sg} show="both" />   Owner dash — the iron map and the GPS map side by side
//    <FleetMapsMini sg={sg} show="gps" />    PM board   — the GPS map alone
//    <FleetMapsMini sg={sg} show="all" />    Yard dash  — ONE integrated map: trackers as dots, rigs as diamonds (#FLEET_MAP_ALL_V1)
//  It reads the same three records the Fleet dash does and nothing else, and writes nothing:
//    fleet-locations   Geotab trackers (api/fleet-locations-sync.js, every 15 min)   → GPS map
//    fleet-oem-hours   factory feeds (api/oem-hours-sync.js, every 15 min)           → iron map
//    fleet-equipment   the Equipment Master, for DUC unit numbers (matched by Serial / VIN)
//  The two feeds are re-read every 5 minutes while the screen is open. Each map has its colour key underneath with
//  live counts, and "⤢ Open" shows it full screen — ✕ or Esc closes it. The iron map shows the same rigs the Rig
//  Board does: on the Equipment Master and heard from recently.
// ══════════════════════════════════════════════════════════
import { useEffect, useMemo, useState } from "react";
import FleetGpsMap from "./FleetGpsMap";
import FleetRigMap from "./FleetRigMap";
import { OEM_KEY, oemActivity, oemFaults, vinLoose } from "./oemFeed";
import { LOC_KEY, locWhen, locLiveMerge, LIVE_URL, LIVE_EVERY_MS } from "./fleetLocations";   // #FLEET_LIVE_V1

const MASTER_KEY = 'fleet-equipment';
const GOOD = '#4ADE80', WARN = '#FFB020', BAD = '#F87171', STEEL = '#9AA0A8';
const rClass = (cat) => { const m = /\bR\s*(\d)\b/i.exec(String(cat || '')); return m ? `R${m[1]}` : ''; };
const pad2 = (n) => String(n).padStart(2, '0');
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
// The Master is stored as columns + rows; only four columns are needed here.
const masterRows = (s) => {
  if (!s || !Array.isArray(s.cols) || !Array.isArray(s.active)) return [];
  const ix = (c) => s.cols.indexOf(c), iu = ix('unit'), iv = ix('vin'), id = ix('desc'), ic = ix('cat');
  if (iu < 0 || iv < 0) return [];
  return s.active.filter(Array.isArray).map(r => ({ unit: r[iu] || '', vin: r[iv] || '', desc: id >= 0 ? (r[id] || '') : '', cat: ic >= 0 ? (r[ic] || '') : '' })).filter(r => r.unit);
};

// One dot per rig a factory feed is reporting on — same rules as the Rig Board's own map.
export const rigPoints = (blob, master) => {
  const byVin = {};
  (master || []).forEach((r) => { const k = vinLoose(r.vin); if (k && k.length >= 5 && !byVin[k]) byVin[k] = r; });
  const day = todayISO();
  return ((blob && Array.isArray(blob.devices)) ? blob.devices : []).map((d) => {
    if (!d || d.deactivated || d.lat == null || d.lon == null) return null;
    const row = byVin[vinLoose(d.vin)];
    if (!row) return null;                                   // not on the Equipment Master (sold, traded) — not DUC's iron any more
    const act = oemActivity(d, blob.generatedAt);
    if (act.state === 'silent' || act.state === 'none') return null;
    const flt = oemFaults(d);
    const ws = act.state === 'active' ? String(d.workingStatus || '').toUpperCase() : '';
    const working = /WORK/.test(ws), idling = /IDL/.test(ws);
    const hrs = Number(d.engineHours);
    return { unit: String(row.unit), name: [rClass(row.cat), String(row.desc || '').trim() || d.model || ''].filter(Boolean).join(' · '),
      lat: Number(d.lat), lon: Number(d.lon), color: working ? GOOD : (idling ? WARN : STEEL),
      ring: flt.tone === 'bad' ? BAD : (flt.tone === 'warn' ? '#FFFFFF' : ''), glow: flt.tone === 'bad' ? BAD : (flt.tone === 'warn' ? WARN : ''),
      status: working ? 'Working' : (idling ? 'Idling' : (act.state === 'parked' ? `Parked ${act.days} day${act.days === 1 ? '' : 's'}` : 'Off — ran today')),
      hours: isFinite(hrs) && hrs > 0 ? hrs.toLocaleString('en-US', { maximumFractionDigits: 1 }) : '',
      fault: flt.total ? `${flt.total} fault code${flt.total === 1 ? '' : 's'} open${flt.text ? ' — ' + flt.text : ''}` : '',
      when: locWhen(d.locationAt || act.at, day), seenAt: d.locationAt || act.at || null };   // #RIG_MAP_STALE_V1
  }).filter(p => p && isFinite(p.lat) && isFinite(p.lon));
};

// show: 'both' | 'gps' | 'iron' | 'all'      tile: the host dashboard's own tile style (so the maps sit in its look)
// ss / upn (optional) — #FLEET_GPS_ASSIGN_V1: with them the GPS map's unit cards get "⚑ Wrong name? Flag to Jerry".
export default function FleetMapsMini({ sg, ss, upn, show = 'both', tile, minHeight = 240 }) {
  const one = show === 'all';                  // the integrated map reads the rigs' positions out of fleet-locations itself
  const wantGps = show !== 'iron', wantIron = show !== 'gps' && !one;
  const [loc, setLoc] = useState(null);
  const [oem, setOem] = useState(null);
  const [master, setMaster] = useState([]);
  useEffect(() => {
    if (!sg) return undefined;
    let dead = false;
    const load = async (first) => {
      if (first) { try { const m = await sg(MASTER_KEY); if (!dead) setMaster(masterRows(m)); } catch { /* maps still draw, by tracker name */ } }
      if (wantGps) { try { const s = await sg(LOC_KEY); if (!dead) setLoc(s && Array.isArray(s.units) ? s : { units: [] }); } catch { if (!dead && first) setLoc(p => p || { units: [] }); } }
      if (wantIron) { try { const s = await sg(OEM_KEY); if (!dead) setOem(s && Array.isArray(s.devices) ? s : { devices: [] }); } catch { if (!dead && first) setOem(p => p || { devices: [] }); } }
    };
    load(true);
    const t = setInterval(() => load(false), 2 * 60 * 1000);   // LOC_5MIN_V1
    return () => { dead = true; clearInterval(t); };
  }, [sg, wantGps, wantIron]);

  // #FLEET_LIVE_V1 — while this map is on screen, Geotab's latest every 30 s (paused when the tab is hidden)
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!wantGps) return undefined;
    let dead = false;
    const tick = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        const s = JSON.parse(localStorage.getItem('dope_sso') || 'null');
        const tok = s && s.access_token;
        if (!tok) return;
        const r = await fetch(LIVE_URL, { headers: { Authorization: `Bearer ${tok}` }, cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        if (!dead && d && d.ok) setLive(d);
      } catch { /* the 5-minute record still shows */ }
    };
    tick();
    const t = setInterval(tick, LIVE_EVERY_MS);
    const vis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', vis);
    return () => { dead = true; clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, [wantGps]);
  const locShown = useMemo(() => locLiveMerge(loc, live), [loc, live]);

  const rigs = useMemo(() => (wantIron ? rigPoints(oem, master) : []), [wantIron, oem, master]);
  // The maps keep their own dark steel look (the street tiles are tinted to it); only the frame comes from the host.
  const T = { panel: '#22252A', panelBorder: '#3A3D42', accent: '#FF7A1A', accent2: WARN, steel: STEEL, text: '#EDEEF0', good: GOOD };
  const cell = { flex: '1 1 210px', minWidth: 0, minHeight };
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch', height: '100%' }}>
      {wantIron ? (
        <div style={cell}>
          <FleetRigMap points={rigs} theme={T} tile={tile} legend checkedAt={oem && oem.generatedAt ? locWhen(oem.generatedAt, todayISO()) : ''}
            emptyNote={oem === null ? 'Reading the rig positions…' : 'Iron map — no rig positions yet.'} />
        </div>
      ) : null}
      {wantGps ? <div style={cell}><FleetGpsMap compact iron={one} blob={locShown} units={master} theme={T} tile={tile} sg={sg} ss={ss} upn={upn} /></div> : null}
    </div>
  );
}
